import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { FL, fmt } from "@/lib/freelance";

// Inline "Applicable amount" box per record row: type → Enter/blur → confirm popover → approve endpoint
export function InlinePrice({ r, onDone }) {
  const pending = r.approval_status === "pending";
  const rejected = r.approval_status === "rejected";
  const [val, setVal] = useState(pending ? "" : String(r.applicable_amount ?? ""));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = Number(val);
  const pct = Number(r.split_percentage) || 0;
  const changed = n !== Number(r.applicable_amount) || pending;
  const ask = () => { if (val !== "" && n > 0 && changed) setOpen(true); };
  const cancel = () => { setOpen(false); setVal(pending ? "" : String(r.applicable_amount ?? "")); };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${FL}/records/${r.id}/approve`, { applicable_amount: n, split_percentage: pct, note: r.approval_note || "" });
      toast.success(pending ? "Pricing confirmed" : "Pricing updated"); onDone(data); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save pricing"); }
    finally { setBusy(false); }
  };
  if (rejected) return <span className="text-slate-600 line-through">{fmt(r.applicable_amount)}</span>;
  return (
    <Popover open={open} onOpenChange={(o) => !o && cancel()}>
      <PopoverTrigger asChild>
        <input type="number" min="0" step="0.01" inputMode="decimal" value={val} placeholder={pending ? "Enter S$" : ""}
          onChange={(e) => setVal(e.target.value)} onBlur={ask} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ask(); } if (e.key === "Escape") cancel(); }}
          data-testid="inline-price-input"
          className={`w-[104px] h-8 rounded-md [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none border bg-slate-950/60 px-2 text-right font-mono-num text-sm outline-none focus:ring-1 focus:ring-teal-400 ${pending ? "border-amber-500/50 text-amber-100 placeholder:text-amber-300/50" : "border-white/[0.08] text-slate-100 hover:border-slate-600"}`} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 bg-[#0F1522] border-slate-700 text-slate-100 p-3 space-y-2" data-testid="inline-price-confirm">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{pending ? "Confirm pricing" : "Update pricing"}</div>
        <div className="text-sm"><span className="font-mono-num font-bold">{r.vehicle_number || r.work_category_label || "Misc"}</span><span className="text-slate-500"> · {r.worker_name}</span></div>
        <div className="flex items-baseline justify-between text-sm"><span className="text-slate-400">Applicable</span><span className="font-mono-num font-bold" data-testid="inline-price-amount">{fmt(n)}</span></div>
        <div className="flex items-baseline justify-between text-sm"><span className="text-slate-400">Worker share ({pct}%)</span><span className="font-mono-num font-black text-teal-200" data-testid="inline-price-share">{fmt(n * pct / 100)}</span></div>
        <div className="flex gap-2 justify-end pt-1">
          <Button variant="outline" size="sm" onClick={cancel} disabled={busy} data-testid="inline-price-cancel" className="h-8 border-slate-700 bg-transparent"><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
          <Button size="sm" onClick={confirm} disabled={busy} data-testid="inline-price-ok" className="h-8 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold"><Check className="w-3.5 h-3.5 mr-1" /> {busy ? "Saving…" : "Confirm"}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
